import { createTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { PaletteOptions } from '@mui/material/styles/createPalette';
import { ThemeOptions } from '@mui/material/styles/createTheme';
import { pickBy } from 'lodash';

const primaryColor = '#05a4d8';
const secondaryColor = '#ffc000';
const darkSurfaceColor = '#fcfcfc';
const textPrimaryColor = '#587985';
const tableHeadColor = '#aaaaaa';

export const themeColors = {
  background: {
    grey: 'rgba(0,0,0,0.05)',
  },
};

// Layout Z-index values for different parts of UI
export const layoutZIndex = {
  portal: {
    footer: 1000,
  },
};

type GenerateThemeSettings = {
  primaryColor: string;
  textPrimaryColor: string;
  secondaryColor: string;
  backgroundColor?: string;
  isDarkTheme?: boolean;
};

const defaultThemeSettings: GenerateThemeSettings = {
  primaryColor,
  textPrimaryColor,
  secondaryColor,
};

const { palette } = createTheme();

const generateThemePalette = (
  props: GenerateThemeSettings,
): { palette: PaletteOptions } => {
  const { primaryColor, secondaryColor, isDarkTheme } = props;

  const validateColor = (color: string, defaultColor: string) => {
    return !!color ? color : defaultColor;
  };

  return {
    palette: {
      mode: isDarkTheme ? 'dark' : 'light',
      primary: palette.augmentColor({
        color: {
          main: validateColor(primaryColor, defaultThemeSettings.primaryColor),
        },
      }),
      secondary: palette.augmentColor({
        color: {
          main: validateColor(
            secondaryColor,
            defaultThemeSettings.secondaryColor,
          ),
        },
      }),
    },
  };
};

const generateSolaForceTheme = (props: GenerateThemeSettings): ThemeOptions => {
  const { primaryColor } = props;

  const { palette } = generateThemePalette(props);

  return {
    components: {
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            color: textPrimaryColor,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          '@global': {
            '*': {
              scrollbarWidth: 'thin',
              scrollbarColor: '#CDCDCD #F0F0F0',
            },
            '*::-webkit-scrollbar': {
              width: 7.5,
              height: 7.5,
            },
            '*::-webkit-scrollbar-track': {
              backgroundColor: '#F0F0F0',
            },
            '*::-webkit-scrollbar-thumb': {
              backgroundColor: '#CDCDCD',
              borderRight: '1px solid white',
              borderBottom: '1px solid white',
              '&:hover': {
                backgroundColor: '#A6A6A6',
              },
              '&:active': {
                backgroundColor: '#606060',
              },
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          input: {
            '&[placeholder]': {
              textOverflow: 'ellipsis',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: tableHeadColor,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: darkSurfaceColor,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: 'bold',
            minWidth: 'inherit',
            textTransform: 'none',
            width: 'inherit',
          },
          textColorInherit: {
            color: textPrimaryColor,
            opacity: 1,
            '&$selected': {
              color: primaryColor,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: primaryColor,
            height: 4,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: ({ ownerState }) => {
            const color = ownerState.disabled ? 'inherit' : '#db3131';

            return {
              color,
              '&$error': {
                color,
              },
            };
          },
        },
      },
    },
    palette,
  };
};

export const createSolaForceTheme = (
  settings: Partial<GenerateThemeSettings>,
) =>
  createTheme(
    generateSolaForceTheme({
      ...defaultThemeSettings,
      ...pickBy(settings, (v) => v !== undefined),
    }),
  );

export const theme = createTheme({
  components: {
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          color: textPrimaryColor,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: '#CDCDCD #F0F0F0',
          },
          '*::-webkit-scrollbar': {
            width: 7.5,
            height: 7.5,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: '#F0F0F0',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: '#CDCDCD',
            borderRight: '1px solid white',
            borderBottom: '1px solid white',
            '&:hover': {
              backgroundColor: '#A6A6A6',
            },
            '&:active': {
              backgroundColor: '#606060',
            },
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          '&[placeholder]': {
            textOverflow: 'ellipsis',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: tableHeadColor,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: darkSurfaceColor,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          minWidth: 'inherit',
          textTransform: 'none',
          width: 'inherit',
        },
        textColorInherit: {
          color: textPrimaryColor,
          opacity: 1,
          '&$selected': {
            color: primaryColor,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: primaryColor,
          height: 4,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: ({ ownerState }) => {
          const color = ownerState.disabled ? 'inherit' : '#db3131';

          return {
            color,
            '&$error': {
              color,
            },
          };
        },
      },
    },
  },
  palette: {
    primary: {
      contrastText: grey[50],
      main: primaryColor,
    },
    secondary: {
      contrastText: grey[50],
      main: secondaryColor,
    },
  },
});
